/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import styles from './styles.module.scss';
import Image, { akamaiLoader, pureLoader } from '@/components/NextImage';
import root from 'window-or-global';
import { replaceHistoryStateOptionsOnClick } from '@/features/common/utils/replaceHistoryStateOptionsOnClick';
import { historyStateOptionAttributes } from '@/constants/historyStateOption';
import { useActiveElement } from '@/hooks/useActiveElement';
import { useLabels } from '@/hooks/useLabels';
import { Button } from '@/components/Button';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { useAddToCart } from '@/features/orders/api/postAddToCart';
import { useAddToCartWithMiniCart } from '../../hooks/useAddToCartWithMiniCart';
import type {
  AddCertonaItemObjectType,
  AddToCartRequestType,
  HorizontalPartCardProps,
} from '../../interface';
import { SmartLink as Link } from '@/utils/smartLink';
import { Rating } from '@/components/Rating/Rating';
import Hidden from '@/components/Hidden';
import { LoadingIndicatorDots } from '@/components/LoadingIndicatorDots';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useRouter } from 'next/navigation';
import { routePaths } from '@/constants/routePaths';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useDeviceType } from '@/utils/useDeviceType';
import { usePaymentOptionsMutation } from '@/features/checkout/api/getPaymentOptions';
import { formatPrice } from '@/utils/validator/formatPrice';
import { type OrderType } from '@/constants/orderTypes';
import type { CustomLinkName } from '@/types/analytics';
import { View, Text } from '@az/starc-ui';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import { getPageTypeDisplayName } from '@/utils/analytics/track/trackRecommendationsPageOrZone';

const labelMap = {
  priceUnavailable: 'label_ProductDisplayPage_body_PriceUnavailable',
  lblCarouselImage: 'label_Carousel_Image',
  lblAdd: 'label_MyAccountVehicle_maintenanceInterval_Add',
  lblToCart: 'label_ProductDisplayPage_body_ToCart',
  lblAddToCart: 'label_add_to_cart_certona',
  lblAdded: 'label_added_to_cart_certona',
  lblTotalReviews: 'label_Carousel_TotalReviews',
  lblOptions: 'label_certonaOptions',
};

export const HorizontalPartCard = (props: HorizontalPartCardProps) => {
  const {
    part: { properties, label: productLinkName, totalReviews, averageRating },
    actionHandler,
    itemId = '',
    pricingInfo,
    dynamicRef,
    onkeydown,
    addToCartAnalyticsandSuccessMessage,
    showCard = true,
    addToCartData,
    showAddToCartCTA,
    shouldShowAddToCart = true,
    isLoadingAddToCart,
    certonaType,
    setAddedCertonaItemObject,
    updateAfterAddtoCartClick,
    inDrawer,
    cardStyle,
    getCtjAnalytics,
  } = props;

  const { priceUnavailable, lblAdd, lblToCart, lblAdded, lblTotalReviews, lblOptions } =
    useLabels(labelMap);
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const { mutateAsync: fetchPaymentOptions } = usePaymentOptionsMutation();
  const isOosOrDoesNotFit = certonaType === 'OOS' || certonaType === 'DNF';
  const isCompleteTheJob = certonaType === 'CTJ';
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.only('md'));
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const deviceType = useDeviceType();
  const isBot = deviceType === 'bot';
  const certonaAddToCartEnabled = !isBot && showAddToCartCTA;
  const locale = useLocale();
  const isMx = locale === countryCodes.mx;
  const [showLoadingIndicators, setShowLoadingIndicators] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const { addToCartCall, addingToCartStatus, cartSuccess } = useAddToCartWithMiniCart(inDrawer);
  const addProductToCart = `${lblAdd} ${productLinkName} ${lblToCart}`;
  const seeOptionsForProduct = `${lblOptions} ${productLinkName}`;
  const { corePrice, retailPrice, totalPrice } = pricingInfo ?? {};
  const showOptions = !shouldShowAddToCart;
  const focusedElement = useActiveElement();
  const linkRef = useRef<HTMLAnchorElement>(null);
  const isCart = legacyRouter.asPath === routePaths.cart;
  const { setPageSpecificData } = usePageSpecificData();
  const { acesEnabled } = useAcesFlags();

  const scrollIntoViewOptions: ScrollIntoViewOptions = {
    block: 'nearest',
    inline: 'nearest',
    behavior: 'auto',
  };
  const { data: storeDetailsData } = useStoreDetailsData();
  const addToCartMutation = useAddToCart();

  const submitToCart = () => {
    router.push(routePaths.cart);
  };

  const addCertonaItemObject: AddCertonaItemObjectType = {
    productImage: properties?.imageUrl ?? '',
    cartSuccessData: cartSuccess,
    productDetails: {
      productName: {
        productBrand: addToCartData?.brandName ?? '',
        productPart: addToCartData?.productPartNumber ?? '',
        quickNote: addToCartData?.productRepositoryId ?? '',
      },
      skuId: addToCartData?.skuId,
      partType: addToCartData?.originalPartTypeId?.toString(),
      productId: addToCartData?.productId,
    },
    submitToCart,
    productTitle: productLinkName ?? '',
    pageName: 'pdp',
  };

  const afterAddTocartSuccess = () => {
    setShowLoadingIndicators(false);
    setAddedToCart(true);
    const addToCartAnalyticsData = {
      price: totalPrice?.toFixed(2),
      imageUrl: properties.imageUrl,
      label: productLinkName,
      ...addToCartData,
    };
    if (addToCartAnalyticsandSuccessMessage) {
      addToCartAnalyticsandSuccessMessage(addToCartAnalyticsData);
    }
  };

  const newAfterAddToCartSuccess = async () => {
    setShowLoadingIndicators(false);
    setAddedToCart(true);
    await fetchPaymentOptions({});
    const addToCartAnalyticsData = {
      price: totalPrice?.toFixed(2),
      imageUrl: properties.imageUrl,
      label: productLinkName,
      ...addToCartData,
    };
    if (inDrawer) {
      updateAfterAddtoCartClick?.(addToCartAnalyticsData);
    } else if (addToCartAnalyticsandSuccessMessage) {
      addToCartAnalyticsandSuccessMessage(addToCartAnalyticsData);
    }
    if (setAddedCertonaItemObject) {
      setAddedCertonaItemObject(addCertonaItemObject);
    }
  };

  useEffect(() => {
    if (onkeydown && focusedElement === dynamicRef) {
      focusedElement.scrollIntoView(scrollIntoViewOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedElement]);

  const handleClick = () => {
    actionHandler(productLinkName, itemId);
    replaceHistoryStateOptionsOnClick(
      historyStateOptionAttributes.latestSelectedPageYOffset,
      root.pageYOffset,
    );
  };

  const handleNavigateToPDP = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    handleClick();
    router.push(properties.seoUrl);
  };

  const handleAddtoCartCtaClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (addedToCart) {
      return;
    }

    if (addToCartData?.productId && addToCartData?.productId !== 'undefined' && !addedToCart) {
      if (showAddToCartCTA && ![routePaths.cart].includes(legacyRouter.asPath)) {
        return handleAddtoCartNewCtaClick();
      }

      setShowLoadingIndicators(true);
      const addToCartRequest = {
        skuId: String(addToCartData?.skuId),
        productId: addToCartData?.partGroupId ?? addToCartData?.productId,
        quantity: '1',
        fulfillmentTypeId: addToCartData?.fulFillmentId,
        vehicleId: addToCartData?.vehicleId,
        storeNumber: String(storeDetailsData?.storeNumber),
      };
      if (!addToCartMutation.isPending) {
        await addToCartMutation.mutateAsync(addToCartRequest);
        afterAddTocartSuccess();
      }
    } else {
      submitToCart();
    }
  };

  const getLocationDisplayName = () => {
    if (inDrawer) {
      return 'Mini Cart';
    }
    if (root.utag_data?.searchKeyword) {
      return 'Search';
    }
    return getPageTypeDisplayName(root.utag_data?.pageType);
  };

  const handleAddtoCartNewCtaClick = () => {
    setShowLoadingIndicators(true);
    if (addingToCartStatus !== 'pending') {
      const { pageName = '', pageType = '' } = root.utag_data ?? {};

      const onSuccess = () => {
        void newAfterAddToCartSuccess();
      };

      const onSettled = () => {
        setShowLoadingIndicators(false);
      };

      const locationDisplayName = getLocationDisplayName();
      let addToCartAnalyticsDataForCall = {
        price: totalPrice?.toFixed(2) ?? '',
        pageName: pageName,
        customLinkName: `${
          isOosOrDoesNotFit
            ? `PDP${certonaType === 'OOS' ? ' Out of Stock' : " Doesn't fit"}`
            : pageType
        }-Certona:Add_to_Cart` as CustomLinkName,
        pageType: pageType,
        productRecommendationTypeList: isCompleteTheJob ? 'Affinity' : 'Certona',
        productFindingMethod: 'Product Recommendation',
        productRecommendationInteraction: 'productRecommendationInteraction',
        productRecommendationType: isCompleteTheJob
          ? `${locationDisplayName}: Complete The Job`
          : `${locationDisplayName}: Recommended For You`,
        productBrand: addToCartData?.brandName ?? '',
        productStrikeThroughPricing: ['0'],
        corePrice: corePrice,
        fulfillmentChange: '',
        originalPartTypeId: addToCartData?.originalPartTypeId,
        productRepositoryId: addToCartData?.productRepositoryId,
        productPartNumber: addToCartData?.productPartNumber,
        productFulfillmentOptionSeenByCustomer: Number(addToCartData?.fulFillmentId),
        ...(acesEnabled && {
          partGroupId: addToCartData?.partGroupId ?? '',
          partTerminologyId: addToCartData?.partTerminologyId ?? '',
        }),
        ...addToCartData,
      };

      const ctjAnalytics =
        isCompleteTheJob && getCtjAnalytics ? getCtjAnalytics(addToCartData?.skuId || '') : {};

      addToCartAnalyticsDataForCall = {
        ...addToCartAnalyticsDataForCall,
        ...ctjAnalytics,
      };

      const addToCartRequest: AddToCartRequestType = {
        sku_id: String(addToCartData?.skuId),
        product_id: addToCartData?.partGroupId ?? addToCartData?.productId ?? '',
        quantity: '1',
        category_id: '',
        order_type: addToCartData?.fulFillmentId ?? ('' as OrderType),
        catalog_vehicle_id: addToCartData?.vehicleId ?? '',
        store_number: String(storeDetailsData?.storeNumber),
        orderType: '',
        requestFromCertona: true,
      };

      addToCartCall(
        addToCartRequest,
        addToCartAnalyticsDataForCall,
        catalogVehicleId,
        onSuccess,
        onSettled,
      );
    }
  };

  const { data: headerData } = useHeaderData();
  const skusPresentInCart = headerData?.analyticsData?.cartProductSkus;
  const catalogVehicleId = acesEnabled
    ? headerData?.vehicleMap?.vehicleId
    : (headerData?.vehicleMap?.catalogVehicleId ?? '');

  useEffect(() => {
    const skuId = pricingInfo?.skuId || itemId;
    if (skusPresentInCart?.includes(skuId)) {
      setAddedToCart(true);
    }
  }, [skusPresentInCart, itemId, pricingInfo]);

  if (!showCard) {
    return null;
  }

  return (
    <div className={isOosOrDoesNotFit ? styles.horizontalPartCardContainer : ''}>
      <div
        className={cx(styles.horizontalPartCard, {
          [styles.horizontalPartCardForOos]: isOosOrDoesNotFit,
          [styles.horizontalPartCardForCart]: isCart,
          [styles.horizontalScrolled]: cardStyle === 'horizontal_scrolled',
          [styles.horizontalStacked]: cardStyle === 'horizontal_stacked' && !inDrawer,
        })}
      >
        <div
          onClick={() => {
            linkRef.current?.click();
          }}
          className={cx(styles.gridContainerPartCard, {
            [styles.gridContainerPartCardWithoutCta]: !certonaAddToCartEnabled,
          })}
        >
          <div className={styles.certonaPartImgContainer}>
            <Image
              src={properties.imageUrl}
              alt=""
              data-testid="part-img"
              width={64}
              height={64}
              loader={akamaiLoader}
              unoptimized={false}
            />
          </div>
          <div className={styles.productInfo}>
            <View direction="row" align="center" wrap={false}>
              <View direction="column" justify="center" height="100%">
                <Text variant="large-body" weight="bold" className={styles.starcLargeBodyOverride}>
                  {totalPrice === null ? (
                    <span className={styles.pricingInfo}>{priceUnavailable}</span>
                  ) : null}
                  {typeof totalPrice === 'number'
                    ? `$${corePrice ? formatPrice(totalPrice) : formatPrice(retailPrice)}`
                    : null}
                </Text>
              </View>
              <View.Item gapBefore={{ s: 2, m: 1, l: 1 }} />
              {averageRating && Number(averageRating) > 0 && totalReviews ? (
                <>
                  <View direction="column" justify="center" height="100%">
                    <Rating
                      value={Number(averageRating)}
                      max={5}
                      isOosOrDoesNotFit={isOosOrDoesNotFit}
                      showStarFractions
                      isCertonaPartCard={true}
                      ariaLabel={`${productLinkName} rated ${Number(averageRating).toFixed(
                        1,
                      )} out of 5 stars`}
                    />
                  </View>
                  <View.Item gapBefore={{ s: 1, m: 2, l: 2 }} />
                  <View direction="column" justify="center" height="100%">
                    <Text
                      variant="subtitle-regular"
                      className={styles.starcSubtitleRegularOverride}
                    >
                      <span aria-hidden>{Number(averageRating).toFixed(1)}</span>
                      <span>{` (${totalReviews})`}</span>
                      <span className="sr-only">{lblTotalReviews}</span>
                    </Text>
                  </View>
                </>
              ) : null}
            </View>
            <div className={styles.nameAndCTAContainer}>
              <Link
                to={properties.seoUrl}
                title={productLinkName}
                data-testid="part-link"
                ref={linkRef}
                aria-label={[
                  totalPrice === null
                    ? priceUnavailable
                    : typeof totalPrice === 'number'
                      ? `$${corePrice ? formatPrice(totalPrice) : formatPrice(retailPrice)}`
                      : null,

                  averageRating && Number(averageRating) > 0 && totalReviews
                    ? `${Number(averageRating).toFixed(
                        1,
                      )} out of 5 stars, ${totalReviews} ${lblTotalReviews}`
                    : null,

                  productLinkName,
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => {
                  handleClick();
                  const locationDisplayName = getLocationDisplayName();
                  setPageSpecificData({
                    productFindingMethod: 'Product Recommendation',
                    productRecommendationInteraction: 'productRecommendationInteraction',
                    pfmLocation: root.utag_data?.pageType,
                    productRecommendationType: isCompleteTheJob
                      ? `${locationDisplayName}: Complete The Job`
                      : `${locationDisplayName}: Recommended For You`,
                  });
                }}
              >
                <div
                  className={cx(
                    styles.partLabel,
                    styles.certonaPartLabel,
                    styles.partLabelRecommendation,
                    {
                      [styles.certonaPartLabelForOos]: isOosOrDoesNotFit,
                    },
                  )}
                  data-testid="part-label"
                >
                  {productLinkName}
                </div>
              </Link>
              {certonaAddToCartEnabled && !isLoadingAddToCart && (
                <div
                  onClick={(e) => e.stopPropagation() /*prevents <Link/> ancestor onClick */}
                  className={styles.addToCartButtonGridArea}
                >
                  <Hidden smDown implementation="js">
                    {isOosOrDoesNotFit ? (
                      <Button
                        ariaLabel={showOptions ? seeOptionsForProduct : addProductToCart}
                        variant="outlined"
                        shape={isTablet ? 'rounded' : 'pill'}
                        size="small"
                        customClass={cx(
                          isTablet ? styles.addtoCartButtonMobileForOos : styles.addtoCartButton,
                          {
                            [styles.configSkuMobileCTA]: showOptions && !isTablet,
                            [styles.addedTocartButtonstyles]: addedToCart && !isTablet,
                          },
                        )}
                        onClick={showOptions ? handleNavigateToPDP : handleAddtoCartCtaClick}
                      >
                        {!showLoadingIndicators && showOptions && isDesktop && (
                          <span>{lblOptions.toUpperCase()}</span>
                        )}
                        {!showLoadingIndicators &&
                          (!showOptions || (showOptions && isTablet)) &&
                          !addedToCart && (
                            <>
                              <Image
                                src={'/images/addcart.svg'}
                                alt=""
                                width={20}
                                height={20}
                                loader={pureLoader}
                              />
                              {!showLoadingIndicators &&
                                !addedToCart &&
                                !showOptions &&
                                !isTablet && (
                                  <span
                                    className={cx(styles.addtoCartText, {
                                      [styles.addtoCartTextMx]: isMx,
                                    })}
                                  >
                                    {' '}
                                    {lblAdd}
                                  </span>
                                )}
                            </>
                          )}
                        {!showLoadingIndicators &&
                          (!showOptions || (showOptions && isTablet)) &&
                          addedToCart && (
                            <>
                              <Image
                                src={'/images/addedtocart.svg'}
                                alt=""
                                width={20}
                                height={20}
                                loader={pureLoader}
                              />
                              {!showLoadingIndicators &&
                                addedToCart &&
                                !showOptions &&
                                !isTablet && (
                                  <span
                                    className={cx(styles.addtoCartText, {
                                      [styles.addtoCartTextMx]: isMx,
                                    })}
                                  >
                                    {' '}
                                    {lblAdded}
                                  </span>
                                )}
                            </>
                          )}
                        {showLoadingIndicators && <LoadingIndicatorDots size={8} color={'#000'} />}
                      </Button>
                    ) : (
                      <Button
                        ariaLabel={addProductToCart}
                        variant="outlined"
                        shape="pill"
                        size="small"
                        customClass={cx(styles.addtoCartButton, {
                          [styles.addedTocartButtonstyles]: addedToCart,
                        })}
                        onClick={showOptions ? handleNavigateToPDP : handleAddtoCartCtaClick}
                      >
                        {!showLoadingIndicators && showOptions && (
                          <span>{lblOptions.toUpperCase()}</span>
                        )}
                        {!showLoadingIndicators && !addedToCart && !showOptions && (
                          <>
                            <Image
                              src={'/images/addcart.svg'}
                              alt=""
                              width={15}
                              height={15}
                              loader={pureLoader}
                            />
                            <span
                              className={cx(styles.addtoCartText, {
                                [styles.addtoCartTextMx]: isMx,
                              })}
                            >
                              {' '}
                              {lblAdd}
                            </span>
                          </>
                        )}
                        {!showLoadingIndicators && addedToCart && !showOptions && (
                          <>
                            <Image
                              src={'/images/green-tick-rounded.svg'}
                              alt=""
                              width={12}
                              height={12}
                              loader={pureLoader}
                            />
                            <span
                              className={cx(styles.addtoCartText, {
                                [styles.addtoCartTextMx]: isMx,
                              })}
                            >
                              {' '}
                              {lblAdded}
                            </span>
                          </>
                        )}
                        {showLoadingIndicators && <LoadingIndicatorDots size={12} color={'#000'} />}
                      </Button>
                    )}
                  </Hidden>
                  <Hidden mdUp implementation="js">
                    <Button
                      ariaLabel={addProductToCart}
                      variant="outlined"
                      shape={isMobile ? 'rounded' : 'pill'}
                      size="small"
                      customClass={cx(styles.addtoCartButtonMobile, {
                        [styles.configSkuMobileCTA]: showOptions && !isOosOrDoesNotFit,
                      })}
                      onClick={showOptions ? handleNavigateToPDP : handleAddtoCartCtaClick}
                    >
                      {!showLoadingIndicators && showOptions && !isOosOrDoesNotFit && (
                        <span>{lblOptions.toUpperCase()}</span>
                      )}
                      {!showLoadingIndicators &&
                        (!showOptions || (showOptions && isOosOrDoesNotFit)) &&
                        !addedToCart && (
                          <Image
                            src={'/images/addcart.svg'}
                            alt=""
                            width={20}
                            height={20}
                            loader={pureLoader}
                          />
                        )}
                      {!showLoadingIndicators &&
                        (!showOptions || (showOptions && isOosOrDoesNotFit)) &&
                        addedToCart && (
                          <Image
                            src={'/images/addedtocart.svg'}
                            alt=""
                            width={20}
                            height={20}
                            loader={pureLoader}
                          />
                        )}
                      {showLoadingIndicators && <LoadingIndicatorDots size={8} color={'#000'} />}
                    </Button>
                  </Hidden>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
